import { FC, useEffect, useMemo, useState } from 'react';
import { API, Endpoints, Status } from '@brg/api';
import { Skeleton } from '@brg/ui';
import { useTranslation } from 'react-i18next';
import { bTag } from 'core';
import { useDesignerById } from 'shared/hooks/api/useDesignerById';
import { CreateLinkTo } from 'shared/constants/linkTo';
import { AppTester } from 'supporting';
import { useSelector } from 'react-redux';
import { TagPageSelector } from 'shared/reducers/TagPageSlice';
import { tagPageCategories } from '../TagPageSwitcher';
import CustomShowMore from '../CustomShowMore';
import {
  ClaimButton,
  ContactLink,
  Description,
  HeaderRoot,
  ImageContainer,
  ImageMock,
  ImagePreview,
  InfoColumn,
  MetaRow,
  ProfileLink,
  Title,
  TitleRow,
} from './styles';

export interface IPersonalizedTagProps {
  tag?: any;
  isLoading?: boolean;
  theme: any;
}

const PERSONALIZED_TAG_CLAIM_FORM_PATH = '/teams/form/d5ce6ac6db8a458ca69a1d087fcb2975';

const getLangText = (
  items: Array<{ lang?: string; name?: string }> | string | undefined | null,
  lang: string,
) => {
  if (typeof items === 'string') return items;
  if (!Array.isArray(items) || !items.length) return '';
  return (
    items.find((i) => i.lang?.toLowerCase() === lang.toLowerCase())?.name
    || items.find((i) => i.lang?.toLowerCase() === 'ru')?.name
    || items.find((i) => i.lang?.toLowerCase() === 'en')?.name
    || items[0]?.name
    || ''
  );
};

export const PersonalizedTag: FC<IPersonalizedTagProps> = ({
  tag,
  isLoading,
  theme,
}) => {
  const { t, i18n } = useTranslation();
  const [contactFormId, setContactFormId] = useState('');
  const { currentCategory } = useSelector(TagPageSelector);
  const isCompetitionCategory = !!currentCategory && tagPageCategories.competitions === currentCategory;
  const isDarkDescription = AppTester.isGeograffee() || AppTester.isAcademy() || isCompetitionCategory;

  const accountId = tag?.personalzedAccountId || tag?.personalizedAccountId || '';
  const hasLinkedProfile = !!accountId;
  const showClaimButton = !hasLinkedProfile;
  const [designer] = useDesignerById(accountId, i18n.language, accountId);

  const title = useMemo(() => String(bTag.title(tag) || ''), [tag]);

  const description = useMemo(() => {
    return getLangText(tag?.description, i18n.language);
  }, [tag, i18n.language]);

  const imageUrl = useMemo(() => {
    const image = tag?.images?.find((img: any) => img.size === 'original') || tag?.images?.[0];
    if (image?.url) {
      return `${Endpoints.files}/${image.url}`;
    }
    // MOCK: пока url с бэка не приходит
    return null;
  }, [tag]);

  const initials = useMemo(() => {
    const value = title?.trim();
    if (!value) return '?';
    return value.slice(0, 1).toUpperCase();
  }, [title]);

  const designerId = designer?.designerId || '';
  const contactFormPath = (contactFormId && designerId)
    ? `/contact-form/${designerId}/${contactFormId}`
    : '';
  const profilePath = useMemo(() => {
    if (!hasLinkedProfile) return '';
    if (designer) return CreateLinkTo.designerNew(designer);
    return CreateLinkTo.designer(accountId);
  }, [hasLinkedProfile, designer, accountId]);

  useEffect(() => {
    if (!accountId) {
      setContactFormId('');
      return;
    }

    API.request()
      .endpoint(Endpoints.tagsMnePw)
      .url('/api/TagsResponsibility/GetUserInfo')
      .addQuery('accountId', accountId)
      .get()
      .send()
      .then((response: any) => (typeof response?.json === 'function' ? response.json() : response))
      .then((data: any) => {
        const list: Status[] = Array.isArray(data) ? data : (Array.isArray(data?.data) ? data.data : []);
        const contactStatus = list.find((status) => status?.canContact && status?.formId);
        setContactFormId(contactStatus?.formId || '');
      })
      .catch(() => {
        setContactFormId('');
      });
  }, [accountId]);

  const metaContent = showClaimButton ? (
    <MetaRow>
      <ClaimButton to={PERSONALIZED_TAG_CLAIM_FORM_PATH}>
        {t('personalized_tag.claim_button')}
      </ClaimButton>
    </MetaRow>
  ) : (
    <MetaRow>
      {!!contactFormPath && (
        <ContactLink to={contactFormPath}>
          {t('illustration_page.contact')}
        </ContactLink>
      )}
    </MetaRow>
  );

  const header = (isLoading && !tag) ? (
    <HeaderRoot>
      <ImageContainer>
        <Skeleton
          style={{ backgroundColor: theme.skeleton.skeletonBgColor }}
          width="100%"
          height="100%"
        />
      </ImageContainer>
      <InfoColumn>
        <Skeleton
          style={{ backgroundColor: theme.skeleton.skeletonBgColor }}
          width={294}
          height={50}
        />
        <Skeleton
          style={{ backgroundColor: theme.skeleton.skeletonBgColor, marginTop: 8 }}
          width={160}
          height={22}
        />
        <Skeleton
          style={{ backgroundColor: theme.skeleton.skeletonBgColor, marginTop: 16 }}
          width="100%"
          height={84}
        />
      </InfoColumn>
    </HeaderRoot>
  ) : (
    <HeaderRoot>
      <ImageContainer>
        {imageUrl ? (
          <ImagePreview src={imageUrl} alt={title} />
        ) : (
          <ImageMock>{initials}</ImageMock>
        )}
      </ImageContainer>

      <InfoColumn>
        <TitleRow>
          <Title>{title}</Title>
          {!!profilePath && (
            <ProfileLink to={profilePath}>
              {t('personalized_tag.profile')}
              <svg width="8" height="22" viewBox="0 0 8 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M1.21474 17L0 15.786L5.12833 10.6559H1.27573L1.2859 9H8V15.7149H6.33291L6.34307 11.8698L1.21474 17Z"
                  fill="currentColor"
                />
              </svg>
            </ProfileLink>
          )}
        </TitleRow>

        {metaContent}

        {!!description && (
          <Description>
            <CustomShowMore
              text={description}
              maxLines={6}
              isDark={isDarkDescription}
            />
          </Description>
        )}
      </InfoColumn>
    </HeaderRoot>
  );

  return header;
};
import { FC, useEffect, useMemo, useState } from 'react';
import { API, Endpoints, Status } from '@brg/api';
import { Skeleton } from '@brg/ui';
import { useTranslation } from 'react-i18next';
import { bTag } from 'core';
import { useDesignerById } from 'shared/hooks/api/useDesignerById';
import { CreateLinkTo } from 'shared/constants/linkTo';
import { AppTester } from 'supporting';
import { useSelector } from 'react-redux';
import { TagPageSelector } from 'shared/reducers/TagPageSlice';
import { tagPageCategories } from '../TagPageSwitcher';
import CustomShowMore from '../CustomShowMore';
import {
  ClaimButton,
  ContactLink,
  Description,
  HeaderRoot,
  ImageContainer,
  ImageMock,
  ImagePreview,
  InfoColumn,
  MetaRow,
  ProfileLink,
  Title,
  TitleRow,
} from './styles';

export interface IPersonalizedTagProps {
  tag?: any;
  isLoading?: boolean;
  theme: any;
}

const PERSONALIZED_TAG_CLAIM_FORM_PATH = '/teams/form/d5ce6ac6db8a458ca69a1d087fcb2975';

const getLangText = (
  items: Array<{ lang?: string; name?: string }> | string | undefined | null,
  lang: string,
) => {
  if (typeof items === 'string') return items;
  if (!Array.isArray(items) || !items.length) return '';
  return (
    items.find((i) => i.lang?.toLowerCase() === lang.toLowerCase())?.name
    || items.find((i) => i.lang?.toLowerCase() === 'ru')?.name
    || items.find((i) => i.lang?.toLowerCase() === 'en')?.name
    || items[0]?.name
    || ''
  );
};

export const PersonalizedTag: FC<IPersonalizedTagProps> = ({
  tag,
  isLoading,
  theme,
}) => {
  const { t, i18n } = useTranslation();
  const [contactFormId, setContactFormId] = useState('');
  const { currentCategory } = useSelector(TagPageSelector);
  const isCompetitionCategory = !!currentCategory && tagPageCategories.competitions === currentCategory;
  const isDarkDescription = AppTester.isGeograffee() || AppTester.isAcademy() || isCompetitionCategory;

  const accountId = tag?.personalzedAccountId || tag?.personalizedAccountId || '';
  const hasLinkedProfile = !!accountId;
  const showClaimButton = !hasLinkedProfile;
  const [designer] = useDesignerById(accountId, i18n.language, accountId);

  const title = useMemo(() => String(bTag.title(tag) || ''), [tag]);

  const description = useMemo(() => {
    return getLangText(tag?.description, i18n.language);
  }, [tag, i18n.language]);

  const imageUrl = useMemo(() => {
    const image = tag?.images?.find((img: any) => img.size === 'original') || tag?.images?.[0];
    if (image?.url) {
      return `${Endpoints.files}/${image.url}`;
    }
    // MOCK: пока url с бэка не приходит
    return null;
  }, [tag]);

  const initials = useMemo(() => {
    const value = title?.trim();
    if (!value) return '?';
    return value.slice(0, 1).toUpperCase();
  }, [title]);

  const designerId = designer?.designerId || '';
  const contactFormPath = (contactFormId && designerId)
    ? `/contact-form/${designerId}/${contactFormId}`
    : '';
  const profilePath = useMemo(() => {
    if (!hasLinkedProfile) return '';
    if (designer) return CreateLinkTo.designerNew(designer);
    return CreateLinkTo.designer(accountId);
  }, [hasLinkedProfile, designer, accountId]);

  useEffect(() => {
    if (!accountId) {
      setContactFormId('');
      return;
    }

    API.request()
      .endpoint(Endpoints.tagsMnePw)
      .url('/api/TagsResponsibility/GetUserInfo')
      .addQuery('accountId', accountId)
      .get()
      .send()
      .then((response: any) => (typeof response?.json === 'function' ? response.json() : response))
      .then((data: any) => {
        const list: Status[] = Array.isArray(data) ? data : (Array.isArray(data?.data) ? data.data : []);
        const contactStatus = list.find((status) => status?.canContact && status?.formId);
        setContactFormId(contactStatus?.formId || '');
      })
      .catch(() => {
        setContactFormId('');
      });
  }, [accountId]);

  const metaContent = showClaimButton ? (
    <MetaRow>
      <ClaimButton to={PERSONALIZED_TAG_CLAIM_FORM_PATH}>
        {t('personalized_tag.claim_button')}
      </ClaimButton>
    </MetaRow>
  ) : (
    <MetaRow>
      {!!contactFormPath && (
        <ContactLink to={contactFormPath}>
          {t('illustration_page.contact')}
        </ContactLink>
      )}
    </MetaRow>
  );

  const header = (isLoading && !tag) ? (
    <HeaderRoot>
      <ImageContainer>
        <Skeleton
          style={{ backgroundColor: theme.skeleton.skeletonBgColor }}
          width="100%"
          height="100%"
        />
      </ImageContainer>
      <InfoColumn>
        <Skeleton
          style={{ backgroundColor: theme.skeleton.skeletonBgColor }}
          width={294}
          height={50}
        />
        <Skeleton
          style={{ backgroundColor: theme.skeleton.skeletonBgColor, marginTop: 8 }}
          width={160}
          height={22}
        />
        <Skeleton
          style={{ backgroundColor: theme.skeleton.skeletonBgColor, marginTop: 16 }}
          width="100%"
          height={84}
        />
      </InfoColumn>
    </HeaderRoot>
  ) : (
    <HeaderRoot>
      <ImageContainer>
        {imageUrl ? (
          <ImagePreview src={imageUrl} alt={title} />
        ) : (
          <ImageMock>{initials}</ImageMock>
        )}
      </ImageContainer>

      <InfoColumn>
        <TitleRow>
          <Title>{title}</Title>
          {!!profilePath && (
            <ProfileLink to={profilePath}>
              {t('personalized_tag.profile')}
              <svg width="8" height="22" viewBox="0 0 8 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M1.21474 17L0 15.786L5.12833 10.6559H1.27573L1.2859 9H8V15.7149H6.33291L6.34307 11.8698L1.21474 17Z"
                  fill="currentColor"
                />
              </svg>
            </ProfileLink>
          )}
        </TitleRow>

        {metaContent}

        {!!description && (
          <Description>
            <CustomShowMore
              text={description}
              maxLines={6}
              isDark={isDarkDescription}
            />
          </Description>
        )}
      </InfoColumn>
    </HeaderRoot>
  );

  return header;
};
